import { Paragraph } from 'docx';

// Utils
import { isEmptyValueArray } from '@shared/utils';
import {
  buildDateRange,
  buildTechStackText,
  flattenJourneyProjects,
} from '@/utils/resume';
import {
  htmlToBulletLines,
  htmlToSpacedPlainText,
} from '@/utils/htmlSerializer';
import {
  bodyRun,
  bulletParagraph,
  sectionHeading,
  sectionSpacer,
} from '../utils';

// Types
import { TemplateProps } from '@/components/template/resume/type';

// Tokens
import { SECTION_TITLES } from '../../tokens';

// Constants
import { LINE_SPACING, SPACINGS } from '../constants';

export const ExperiencesSection = (
  journeys: TemplateProps['data']['journeys'],
): Paragraph[] => {
  if (!journeys || isEmptyValueArray(journeys)) return [];

  const allProjects = flattenJourneyProjects(journeys);

  if (isEmptyValueArray(allProjects)) return [];

  const paragraphs: Paragraph[] = [sectionHeading(SECTION_TITLES.experience)];

  allProjects.forEach(({ project, role }, projIdx) => {
    const {
      name: projectName = '',
      desc = '',
      duties = '',
      stacks = [],
      start = '',
      end = '',
    } = project ?? {};

    const duration = buildDateRange(start, end);
    const plainDesc = htmlToSpacedPlainText(desc);

    const bulletLines = htmlToBulletLines(duties);
    const techStackText = buildTechStackText(stacks ?? []);
    const projectNumber = projIdx + 1;
    const projectHeader = `Project #${projectNumber}${projectName ? `: ${projectName}` : ''}`;

    // Project #N: projectName (bold header)
    paragraphs.push(
      new Paragraph({
        spacing: {
          before: SPACINGS.entryTitleMarginTop,
          line: LINE_SPACING,
        },
        children: [bodyRun(projectHeader, { bold: true })],
      }),
    );

    // Description (plain text)
    if (plainDesc) {
      paragraphs.push(
        new Paragraph({
          spacing: { line: LINE_SPACING },
          children: [bodyRun(plainDesc)],
        }),
      );
    }

    // Duration: start - end
    if (duration) {
      paragraphs.push(
        new Paragraph({
          spacing: { line: LINE_SPACING },
          children: [bodyRun('Duration: ', { bold: true }), bodyRun(duration)],
        }),
      );
    }

    // Role:
    if (role) {
      paragraphs.push(
        new Paragraph({
          spacing: { line: LINE_SPACING },
          children: [bodyRun('Role: ', { bold: true }), bodyRun(role)],
        }),
      );
    }

    // Responsibility bullets from duties
    for (const line of bulletLines) {
      paragraphs.push(bulletParagraph([bodyRun(line)]));
    }

    // Technical Skills bullet, per-project
    if (techStackText) {
      paragraphs.push(
        bulletParagraph([
          bodyRun('Technical Skills: ', { bold: true }),
          bodyRun(techStackText),
        ]),
      );
    }
  });

  paragraphs.push(sectionSpacer());

  return paragraphs;
};
